import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./EverywherePaywall.module.scss"

// ---------------------------------------------------------------------------
// "Everywhere" — the pitch is continuity, not privacy: one library, in step on
// every screen. The star of the page is an isometric constellation of the
// user's devices wired together; tap the shutter and the photo you just took
// beams out along the wires, lighting up each device "in sync" in turn.
//
// Deliberately NON-photographic and light: photos are abstract gradient tiles,
// so the whole thing renders offline with zero image assets.
// ---------------------------------------------------------------------------

type Device = {
  id: string
  label: string
  kind: "phone" | "laptop" | "tablet" | "watch" | "desktop"
  x: number // screen-centre in the 340×330 scene viewBox
  y: number
}

// The phone (index 0) is the origin — where you shoot. The rest receive.
const DEVICES: Device[] = [
  { id: "phone", label: "iPhone", kind: "phone", x: 170, y: 244 },
  { id: "laptop", label: "MacBook", kind: "laptop", x: 66, y: 150 },
  { id: "desktop", label: "iMac", kind: "desktop", x: 268, y: 138 },
  { id: "watch", label: "Watch", kind: "watch", x: 96, y: 58 },
  { id: "tablet", label: "iPad", kind: "tablet", x: 262, y: 44 },
]

const ORIGIN = DEVICES[0]
const RECEIVERS = DEVICES.slice(1)

// Abstract "photos" — gradient swatches with a tiny glyph. No image files.
const SHOTS: {
  from: string
  to: string
  glyph: "peak" | "sun" | "wave" | "leaf"
}[] = [
  { from: "#ffb347", to: "#ff6a88", glyph: "sun" },
  { from: "#4facfe", to: "#00d4ff", glyph: "wave" },
  { from: "#43e97b", to: "#20c9a6", glyph: "leaf" },
  { from: "#a68cff", to: "#ff7eb3", glyph: "peak" },
  { from: "#ff8a5c", to: "#ffd166", glyph: "peak" },
]

function Glyph({ kind }: { kind: "peak" | "sun" | "wave" | "leaf" }) {
  switch (kind) {
    case "sun":
      return (
        <g>
          <circle cx="0" cy="3" r="3.4" fill="rgba(255,255,255,.92)" />
          <path
            d="M-8 8 L-3 3 L1 6 L6 0 L9 4"
            stroke="rgba(255,255,255,.6)"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
          />
        </g>
      )
    case "wave":
      return (
        <path
          d="M-9 2 q4 -5 8 0 t8 0 t8 0"
          stroke="rgba(255,255,255,.9)"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
      )
    case "leaf":
      return (
        <path d="M-6 6 q6 -12 12 0 q-6 4 -12 0 Z" fill="rgba(255,255,255,.9)" />
      )
    default:
      return (
        <path d="M-9 7 L-3 -3 L1 3 L5 -5 L10 7 Z" fill="rgba(255,255,255,.9)" />
      )
  }
}

// A gradient photo tile centred at (0,0), size s.
function PhotoTile({
  s,
  gid,
  glyph,
}: {
  s: number
  gid: string
  glyph: "peak" | "sun" | "wave" | "leaf"
}) {
  return (
    <g>
      <rect
        x={-s / 2}
        y={-s / 2}
        width={s}
        height={s}
        rx={s * 0.22}
        fill={`url(#${gid})`}
      />
      <g transform={`scale(${s / 34})`} opacity={0.95}>
        <Glyph kind={glyph} />
      </g>
    </g>
  )
}

// Device chassis drawn in the scene coordinate space, centred on (0,0).
function DeviceGlyph({
  device,
  synced,
  shot,
  pulse,
}: {
  device: Device
  synced: boolean
  shot: number
  pulse: boolean
}) {
  const shade = "#c9d6f5"
  const frame = "#20264a"
  const screenGid = `sg${shot % SHOTS.length}`
  const s = SHOTS[shot % SHOTS.length]

  const screen = (w: number, h: number, r = 3) => (
    <g>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={r} fill="#eef2fb" />
      {synced ? (
        <g>
          <rect
            x={-w / 2}
            y={-h / 2}
            width={w}
            height={h}
            rx={r}
            fill={`url(#${screenGid})`}
          />
          <g transform={`scale(${Math.min(w, h) / 46})`}>
            <Glyph kind={s.glyph} />
          </g>
        </g>
      ) : null}
    </g>
  )

  let body
  switch (device.kind) {
    case "phone":
      body = (
        <g>
          <rect x={-26} y={-46} width={52} height={92} rx={12} fill={frame} />
          <g transform="translate(0,0)">{screen(42, 78, 5)}</g>
        </g>
      )
      break
    case "laptop":
      body = (
        <g>
          <rect x={-42} y={-30} width={84} height={56} rx={6} fill={frame} />
          <g>{screen(74, 46, 3)}</g>
          <path d="M-52 30 L52 30 L44 38 L-44 38 Z" fill={shade} />
          <rect x={-52} y={28} width={104} height={4} rx={2} fill={frame} />
        </g>
      )
      break
    case "desktop":
      body = (
        <g>
          <rect x={-50} y={-38} width={100} height={70} rx={7} fill={frame} />
          <g>{screen(88, 58, 3)}</g>
          <rect x={-9} y={32} width={18} height={12} fill={shade} />
          <path d="M-22 44 L22 44 L26 50 L-26 50 Z" fill={shade} />
        </g>
      )
      break
    case "tablet":
      body = (
        <g>
          <rect x={-32} y={-42} width={64} height={84} rx={9} fill={frame} />
          <g>{screen(54, 74, 4)}</g>
        </g>
      )
      break
    default: // watch
      body = (
        <g>
          <rect x={-6} y={-30} width={12} height={60} rx={5} fill={shade} />
          <rect x={-20} y={-20} width={40} height={40} rx={11} fill={frame} />
          <g>{screen(30, 30, 7)}</g>
        </g>
      )
  }

  return (
    <g>
      {/* soft ground shadow */}
      <ellipse cx={0} cy={52} rx={38} ry={9} fill="#2b5ff5" opacity={0.06} />
      {pulse ? (
        <motion.circle
          cx={0}
          cy={0}
          r={40}
          fill="none"
          stroke="#2b5ff5"
          strokeWidth={2}
          initial={{ scale: 0.5, opacity: 0.5 }}
          animate={{ scale: 1.6, opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      ) : null}
      {body}
      <AnimatePresence>
        {synced ? (
          <motion.g
            key="chk"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 22 }}
            transform="translate(24,-30)"
          >
            <circle cx={0} cy={0} r={9} fill="#2fbf71" />
            <path
              d="M-4 0 L-1 3 L4 -3"
              stroke="#fff"
              strokeWidth={2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </motion.g>
        ) : null}
      </AnimatePresence>
    </g>
  )
}

type Beam = { key: number; target: Device; shot: number }

function EverywherePaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // sync state -------------------------------------------------------------
  const [syncedIds, setSyncedIds] = useState<Set<string>>(() =>
    reduce ? new Set(DEVICES.map((d) => d.id)) : new Set(["phone"]),
  )
  const [shot, setShot] = useState(0)
  const [beams, setBeams] = useState<Beam[]>([])
  const [pulseId, setPulseId] = useState<string | null>(null)
  const shotRef = useRef(0)
  const beamKey = useRef(0)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }

  const fire = useCallback(() => {
    if (reduce) return
    clearTimers()
    const next = (shotRef.current + 1) % SHOTS.length
    shotRef.current = next
    setShot(next)
    setPulseId("phone")
    // phone captures instantly; receivers reset then light up as beams land.
    setSyncedIds(new Set(["phone"]))
    const newBeams: Beam[] = RECEIVERS.map((target, i) => ({
      key: beamKey.current++,
      target,
      shot: next,
    }))
    setBeams(newBeams)
    RECEIVERS.forEach((target, i) => {
      const t = setTimeout(
        () => {
          setSyncedIds((prev) => {
            const s = new Set(prev)
            s.add(target.id)
            return s
          })
          setPulseId(target.id)
          const p = setTimeout(() => setPulseId(null), 260)
          timers.current.push(p)
        },
        520 + i * 210,
      )
      timers.current.push(t)
    })
    const clean = setTimeout(
      () => setBeams([]),
      520 + RECEIVERS.length * 210 + 260,
    )
    timers.current.push(clean)
  }, [reduce])

  // auto-demo once on load, then a gentle loop so a glance always sees motion
  useEffect(() => {
    if (reduce) return
    const start = setTimeout(fire, 700)
    const loop = setInterval(fire, 4600)
    return () => {
      clearTimeout(start)
      clearInterval(loop)
      clearTimers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce])

  const syncedCount = syncedIds.size
  const allSynced = syncedCount === DEVICES.length

  const handleClose = () => native.postNativeEvent("close")
  const buy = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }

  return (
    <div className={styles.page}>
      <div className={styles.top}>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="17" height="17">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <motion.div
          className={styles.statusPill}
          animate={allSynced ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 0.4 }}
        >
          <span className={`${styles.dot} ${allSynced ? styles.dotOn : ""}`} />
          {allSynced
            ? "All devices in sync"
            : `Syncing ${syncedCount}/${DEVICES.length}…`}
        </motion.div>
      </div>

      <div className={styles.head}>
        <p className={styles.eyebrow}>CloudVault Premium</p>
        <h1 className={styles.title}>
          Take it here.
          <br />
          It’s <span className={styles.accent}>already there.</span>
        </h1>
        <p className={styles.sub}>
          Every shot lands on every screen the instant you take it. No cable, no
          export, no “email it to myself.”
        </p>
      </div>

      {/* --- the star: the device constellation ---------------------------- */}
      <div className={styles.scene}>
        <svg viewBox="0 0 340 300" className={styles.svg} aria-hidden>
          <defs>
            {SHOTS.map((s, i) => (
              <linearGradient key={i} id={`sg${i}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={s.from} />
                <stop offset="100%" stopColor={s.to} />
              </linearGradient>
            ))}
          </defs>

          {/* wires from phone to every receiver */}
          {RECEIVERS.map((d) => {
            const mx = (ORIGIN.x + d.x) / 2
            const my = (ORIGIN.y + d.y) / 2 - 26
            const active = syncedIds.has(d.id)
            return (
              <path
                key={d.id}
                d={`M${ORIGIN.x} ${ORIGIN.y} Q ${mx} ${my} ${d.x} ${d.y}`}
                fill="none"
                stroke={active ? "#2b5ff5" : "#c2cdec"}
                strokeOpacity={active ? 0.9 : 0.6}
                strokeWidth={active ? 2.4 : 1.6}
                strokeDasharray="1 7"
                strokeLinecap="round"
                className={styles.wire}
              />
            )
          })}

          {/* receivers behind, phone in front */}
          {RECEIVERS.map((d) => (
            <g key={d.id} transform={`translate(${d.x} ${d.y})`}>
              <DeviceGlyph
                device={d}
                synced={syncedIds.has(d.id)}
                shot={shot}
                pulse={pulseId === d.id}
              />
            </g>
          ))}

          {/* flying photo beams */}
          <AnimatePresence>
            {beams.map((b) => (
              <motion.g
                key={b.key}
                initial={{ x: ORIGIN.x, y: ORIGIN.y, scale: 0.4, opacity: 0 }}
                animate={{
                  x: b.target.x,
                  y: b.target.y,
                  scale: 1,
                  opacity: [0, 1, 1, 0.2],
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: "easeInOut" }}
              >
                <PhotoTile
                  s={26}
                  gid={`sg${b.shot % SHOTS.length}`}
                  glyph={SHOTS[b.shot % SHOTS.length].glyph}
                />
              </motion.g>
            ))}
          </AnimatePresence>

          {/* the origin phone, on top */}
          <g transform={`translate(${ORIGIN.x} ${ORIGIN.y})`}>
            <DeviceGlyph
              device={ORIGIN}
              synced
              shot={shot}
              pulse={pulseId === "phone"}
            />
          </g>
        </svg>

        <button
          className={styles.shutter}
          onClick={fire}
          aria-label="Take a photo"
        >
          <span className={styles.shutterRing} />
          <span className={styles.shutterDot} />
        </button>
        <p className={styles.shutterHint}>
          Tap the shutter — watch it land everywhere
        </p>
      </div>

      {/* continuity plank — one line, not a 4-icon grid */}
      <div className={styles.strip}>
        <span>
          Shoot on the couch, edit at your desk, show it on your watch —
        </span>
        <b> it’s the same library, always in step.</b>
      </div>

      {/* --- pricing / connection bar ------------------------------------- */}
      <div className={styles.sheet}>
        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.on : ""}`}
            onClick={() => setPlan("yearly")}
          >
            <div className={styles.planLeft}>
              <span className={styles.planName}>Yearly</span>
              {savings > 0 ? (
                <span className={styles.save}>Save {savings}%</span>
              ) : null}
            </div>
            <div className={styles.planRight}>
              <span className={styles.bigPrice}>{yearlyPrice}</span>
              <span className={styles.per}>/year</span>
              {yearlyPerMonth ? (
                <span className={styles.permo}>
                  just {yearlyPerMonth}/mo, billed yearly
                </span>
              ) : null}
            </div>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.on : ""}`}
            onClick={() => setPlan("monthly")}
          >
            <span className={styles.planName}>Monthly</span>
            <span className={styles.monthPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <motion.button
          className={styles.cta}
          onClick={buy}
          whileTap={{ scale: 0.97 }}
        >
          Sync every device
        </motion.button>
        <p className={styles.reassure}>
          Cancel anytime — your photos stay on every device you own, always.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

export default EverywherePaywall
